import pino, { LogFn, type LevelOrString, type Logger } from "pino";

import { createWrite } from "./utils/pretty";
import { send } from "./utils/sendToBackend";

let rootLogger: Logger | undefined;

interface LoggerContextBase {
    module?: string;
}

const rootBindings: Record<string, unknown> = {};

interface InitLoggingProps {
    commitHash: string;
    appName: string;
    channel?: string;
    logLevel: {
        api: LevelOrString;
        console: LevelOrString;
    };
    isConsoleLogEnabled?: boolean;
    mixin?: () => Record<string, unknown>;
    onError?: (args: Parameters<LogFn>) => void;
}

export function initLogging({
    appName,
    logLevel,
    commitHash,
    channel,
    isConsoleLogEnabled = true,
    mixin,
    onError,
}: InitLoggingProps) {
    rootLogger = pino({
        browser: {
            write: isConsoleLogEnabled ? createWrite(logLevel.console.toLowerCase(), { mixin }) : () => {},
            transmit: {
                level: logLevel.api.toLowerCase(),
                send: (level, logEvent) => {
                    // Browser path: hooks are unsupported; transmit still receives original log args.
                    if (logEvent.level.value >= pino.levels.values.error) {
                        try {
                            onError?.(logEvent.messages as Parameters<LogFn>);
                        } catch {
                            // Must not throw to avoid breaking the logger
                        }
                    }
                    send(level, logEvent, { mixin });
                },
            },
        },
    });
    rootBindings.appName = appName;
    rootBindings.commitHash = commitHash;
    rootBindings.channel = channel;
    rootBindings.surface = "frontend";
}

export function getLogger<TContext extends LoggerContextBase = LoggerContextBase>(
    context: TContext & Record<string, string>,
) {
    if (!rootLogger) throw new Error("initLogging() not yet called");
    const loggerContext = {
        ...context,
        ...rootBindings,
    };
    return rootLogger.child(loggerContext);
}
